import React from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getPassagemDetail } from "../../api/passagensApi";
import PassagemJourneyTimeline from "../../components/Passagens/PassagemJourneyTimeline";
import { fmtMoney } from "./passagemMpFeeUi";
import { passagemStatusLabel } from "./passagemStatusUi";
import { paymentIntentChipColor, paymentIntentDisplayLabel, paymentIntentDisplaySummary, paymentIntentTooltip } from "./passagemPaymentIntentUi";
import {
  formatPaymentEventPrimary,
  formatPaymentEventSecondary,
} from "./passagemPaymentEventUi";

function fmtDateTime(v) {
  if (!v) return "—";
  return new Date(v).toLocaleString("pt-BR");
}

export default function PassagemDetailDrawer({ open, passagemId, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery(
    ["passagem-detail", passagemId],
    () => getPassagemDetail(passagemId),
    { enabled: open && passagemId != null }
  );

  const v = data?.venda;
  const journey = data?.journey;
  const funnel = data?.funnel || [];
  const payments = data?.payments || [];
  const paymentIntent = data?.paymentIntent;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={700}>
            {t("passagensPage.detailTitle", { defaultValue: "Detalhe da passagem" })}
          </Typography>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <Divider />

      {isLoading ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error?.message || String(error)}
        </Alert>
      ) : v ? (
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Chip label={passagemStatusLabel(t, v.status_venda)} size="small" />
            <Typography variant="body2">
              <strong>{t("passagensPage.colClient", { defaultValue: "Cliente" })}:</strong> {v.client_name || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>{t("passagensPage.colRoute", { defaultValue: "Rota" })}:</strong>{" "}
              {[v.origem, v.destino].filter(Boolean).join(" → ")}
            </Typography>
            <Typography variant="body2">
              <strong>{t("passagensPage.colValue", { defaultValue: "Valor" })}:</strong>{" "}
              {Number(v.valor_cobrado || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            </Typography>
            <Typography variant="body2">
              <strong>{t("passagensPage.colPartner", { defaultValue: "Parceiro" })}:</strong> {v.partner_key}
            </Typography>
            <Typography variant="body2">
              <strong>{t("passagensPage.colChannel", { defaultValue: "Canal" })}:</strong>{" "}
              {v.channel_name || v.canal_source || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>{t("passagensPage.colLocator", { defaultValue: "Localizador" })}:</strong> {v.localizador || v.numero_bpe}
            </Typography>
            {v.taxa_contratual != null || v.sobretaxa != null ? (
              <Typography variant="caption" color="text.secondary" display="block">
                {t("passagensPage.feesSummary", { defaultValue: "Taxas" })}:{" "}
                {v.taxa_contratual != null ? (
                  <>
                    {t("passagensPage.feeContractual", { defaultValue: "Taxa contratual" })}{" "}
                    {fmtMoney(v.taxa_contratual)}
                  </>
                ) : null}
                {v.taxa_contratual != null && Number(v.sobretaxa) > 0 ? " · " : null}
                {Number(v.sobretaxa) > 0 ? (
                  <>
                    {t("passagensPage.feeSurcharge", { defaultValue: "Sobretaxa" })} {fmtMoney(v.sobretaxa)}
                  </>
                ) : null}
              </Typography>
            ) : null}
          </Stack>

          {v.id_lead ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => navigate(`/chat/${v.id_lead}`)}
              sx={{ mb: 2 }}
            >
              {t("passagensPage.openConversation", { defaultValue: "Abrir conversa" })}
            </Button>
          ) : null}

          {paymentIntent?.label ? (
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
                {t("passagensPage.paymentIntentTitle", { defaultValue: "Diagnóstico do link (XPay)" })}
              </Typography>
              <Tooltip title={paymentIntentTooltip(paymentIntent)}>
                <Chip
                  size="small"
                  color={paymentIntentChipColor(paymentIntent.scenario)}
                  label={paymentIntentDisplayLabel(paymentIntent)}
                  sx={{ mb: 0.75 }}
                />
              </Tooltip>
              {paymentIntentDisplaySummary(paymentIntent) ? (
                <Typography variant="body2" color="text.secondary">
                  {paymentIntentDisplaySummary(paymentIntent)}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            {t("passagensPage.timeline", { defaultValue: "Jornada" })}
          </Typography>
          <PassagemJourneyTimeline
            events={journey?.events}
            funnel={funnel}
            audit={data?.auditMessages}
          />

          {payments.length ? (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>
                {t("passagensPage.payments", { defaultValue: "Eventos de pagamento" })}
              </Typography>
              <List dense>
                {payments.map((p) => (
                  <ListItem key={p.id}>
                    <ListItemText
                      primary={formatPaymentEventPrimary(t, p)}
                      secondary={formatPaymentEventSecondary(t, p, { fmtMoney, fmtDateTime })}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : null}
        </Box>
      ) : null}
    </Drawer>
  );
}
